import moment from "moment";

const dateFormat: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
};
/**
 * Formats a date string with a custom format pattern.
 * Supports ISO 8601 and "DD/MM/YYYY HH:mm:ss" formats.
 * @param dateString - The date string to format
 * @param format - The output format (default: "DD/MM/YYYY HH:mm:ss")
 * @returns Formatted date string, or empty string if invalid
 */
const formatDate = (dateString: string | null, format = "DD/MM/YYYY HH:mm:ss"): string => {
  if (!dateString) return "";
  const m = moment(normalizeUtcTimestamp(dateString));
  return m.isValid() ? m.format(format) : "";
};

/**
 * Returns a readable time ago string and the number of days since the given date string.
 * example: "2 d", "3 h", "5 m", "Just now"
 */
const timeAgo = (timestamp: string, customLocale?: string) => {
  if (!timestamp) {
    return { timeAgo: "Unknown", diffInDays: 0 };
  }

  const locale = customLocale || "en-US";
  const m = moment(normalizeUtcTimestamp(timestamp));

  if (!m.isValid()) {
    return { timeAgo: "Invalid date", diffInDays: 0 };
  }

  m.locale(locale);
  const now = moment();
  const diffInMs = now.diff(m);
  const diffInDays = Math.floor(diffInMs / (24 * 60 * 60 * 1000));
  const diffInHours = Math.floor(diffInMs / (60 * 60 * 1000));
  const diffInMinutes = Math.floor(diffInMs / (60 * 1000));

  let timeAgoStr: string;

  // Use day format if more than 18 hours to avoid switching from "23 hours ago" to "yesterday"
  if (diffInHours >= 18) {
    const rtf = new Intl.RelativeTimeFormat(locale, {
      numeric: "auto",
      style: "long",
    });
    timeAgoStr = rtf.format(-Math.max(Math.round(diffInMs / (24 * 60 * 60 * 1000)), 1), "day");
  } else if (diffInHours > 0) {
    const rtf = new Intl.RelativeTimeFormat(locale, {
      numeric: "auto",
      style: "long",
    });
    timeAgoStr = rtf.format(-diffInHours, "hour");
  } else if (diffInMinutes > 0) {
    const rtf = new Intl.RelativeTimeFormat(locale, {
      numeric: "auto",
      style: "long",
    });
    timeAgoStr = rtf.format(-diffInMinutes, "minute");
  } else {
    const rtf = new Intl.RelativeTimeFormat(locale, {
      numeric: "auto",
      style: "long",
    });
    timeAgoStr = rtf.format(0, "second"); // "now" in user's language
  }

  return { timeAgo: timeAgoStr, diffInDays, locale };
};

/**
 * Converts a date string (ISO or "dd/MM/yyyy HH:mm:ss" format) to "dd/MM/yyyy HH:mm:ss" in local time.
 */
const convertDateStringToCustomFormat = (dateString: string | null): string => {
  if (!dateString) return "";
  const m = moment(normalizeUtcTimestamp(dateString));
  if (!m.isValid()) {
    throw new Error(`Invalid date format: ${dateString}`);
  }
  return m.format("DD/MM/YYYY HH:mm:ss");
};

/**
 * Converts a Unix timestamp (milliseconds since epoch) to a date string.
 * @param timestamp - Unix timestamp in milliseconds
 * @param format - Output format (default: "DD/MM/YYYY HH:mm:ss")
 * @returns Formatted date string
 */
const convertTimestampToDateString = (timestamp: number, format = "DD/MM/YYYY HH:mm:ss"): string => {
  return moment(timestamp).format(format);
};

/**
 * Converts a Date object to a custom string format.
 * @param date - Date object to format
 * @param format - Output format (default: "YYYY-MM-DD HH:mm:ss")
 * @returns Formatted date string
 */
const convertDateToCustomString = (date: Date, format = "YYYY-MM-DD HH:mm:ss"): string => {
  return moment(date).format(format);
};

/**
 * Ensures that a timestamp string is in UTC format. If the string does not contain timezone information, "Z" is appended.
 * @param timestamp - The timestamp string to normalize
 * @returns Normalized UTC timestamp string
 */
const normalizeUtcTimestamp = (timestamp: string): string => {
  if (!timestamp) return timestamp;
  const hasOffset = /[+-]\d{2}:\d{2}$|[+-]\d{4}$/.test(timestamp);
  const hasZulu = timestamp.endsWith("Z");

  if (!hasOffset && !hasZulu) {
    return timestamp + "Z";
  }

  return timestamp;
};

export {
  dateFormat,
  formatDate,
  timeAgo,
  convertDateStringToCustomFormat,
  convertDateToCustomString,
  normalizeUtcTimestamp,
  convertTimestampToDateString,
};
